import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "../constants";

export const userQueryKeys = {
  all: () => ["users"] as const,
  details: () => [...userQueryKeys.all(), "detail"] as const,
  detail: (userId: string) => [...userQueryKeys.details(), userId] as const,
};

export const annotationQueryKeys = {
  all: () => ["annotations"] as const,
  lists: () => [...annotationQueryKeys.all(), "list"] as const,
  list: (documentId: string) => [...annotationQueryKeys.lists(), documentId] as const,
};

export const participantStatusQueryKeys = {
  all: () => ["participantStatus"] as const,
  details: () => [...participantStatusQueryKeys.all(), "detail"] as const,
  detail: (roundId: string, userId: string) =>
    [...participantStatusQueryKeys.details(), roundId, userId] as const,
};

export const documentQueryKeys = {
  all: () => ["documents"] as const,
  signedUrls: () => [...documentQueryKeys.all(), "signedUrl"] as const,
  signedUrl: (documentId: string) => [...documentQueryKeys.signedUrls(), documentId] as const,
};

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export const titleQueryKeys = {
  all: () => ["titles"] as const,
  lists: () => [...titleQueryKeys.all(), "list"] as const,
  listMy: (
    includeArchived = false,
    pagination?: PaginationParams,
    status?: "draft" | "reviewing" | "approving" | "completed",
    search?: string,
  ) =>
    [
      ...titleQueryKeys.lists(),
      "my",
      includeArchived,
      pagination?.page ?? DEFAULT_PAGE,
      pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
      // Status is already normalized by caller (undefined when "all")
      status,
      // Normalize search - treat empty/whitespace as undefined
      search?.trim() || undefined,
    ] as const,
  listAll: (
    includeAllOpcos = false,
    includeArchived = false,
    pagination?: PaginationParams,
    status?: "draft" | "reviewing" | "approving" | "completed",
    search?: string,
  ) =>
    [
      ...titleQueryKeys.lists(),
      "all",
      includeAllOpcos,
      includeArchived,
      pagination?.page ?? DEFAULT_PAGE,
      pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
      // Status is already normalized by caller (undefined when "all")
      status,
      // Normalize search - treat empty/whitespace as undefined
      search?.trim() || undefined,
    ] as const,
  details: () => [...titleQueryKeys.all(), "detail"] as const,
  detail: (titleId: string) => [...titleQueryKeys.details(), titleId] as const,
  rounds: (titleId: string) => [...titleQueryKeys.all(), "rounds", titleId] as const,
  round: (titleId: string, roundId: string) =>
    [...titleQueryKeys.rounds(titleId), roundId] as const,
  documents: (roundId: string) => [...titleQueryKeys.all(), "documents", roundId] as const,
  document: (roundId: string, documentId: string) =>
    [...titleQueryKeys.documents(roundId), documentId] as const,
  participants: (titleId: string) => [...titleQueryKeys.all(), "participants", titleId] as const,
  internalNotes: (titleId: string) => [...titleQueryKeys.all(), "internalNotes", titleId] as const,
  listBySuppliers: (
    setterIds: string[],
    includeArchived = false,
    pagination?: PaginationParams,
    status?: "draft" | "reviewing" | "approving" | "completed",
    search?: string,
  ) =>
    [
      ...titleQueryKeys.lists(),
      "bySuppliers",
      // Sort to ensure same key regardless of argument order
      [...setterIds].sort(),
      includeArchived,
      pagination?.page ?? DEFAULT_PAGE,
      pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
      status,
      search?.trim() || undefined,
    ] as const,
};
